import { TZDate } from '@date-fns/tz'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Ban,
  Bell,
  CalendarX,
  Check,
  CheckCheck,
  CheckSquare,
  ClipboardCheck,
  Eye,
  FileText,
  type LucideIcon,
  Mail,
  MessageCircle,
  MessageSquareWarning,
  Pencil,
  RefreshCw,
  Save,
  Send,
  SendHorizontal,
  XCircle
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip'
import { type AuditCard, type AuditLabelResolver, humanizeAuditEntry } from './audit-humanizer'
import type { AuditLogEntry } from './models/audit-log'

/** Identidad del cliente para las filas de actor `client` (nombre corto + foto RENAP). */
export type ClientIdentity = {
  /** Nombre a mostrar (p.ej. "Francisca García" = primer nombre + primer apellido). */
  name: string
  /** URL de la foto (RENAP). Si falta, se cae al avatar de letra. */
  photo?: string | null
}

type AuditTimelineProps = {
  entries: AuditLogEntry[]
  /** Resuelve el nombre de un actor interno por id (fallback si el backend no lo selló). */
  resolveActor?: (id: string) => string
  /** Identidad del cliente, para mostrar su nombre y avatar en vez del genérico "Cliente". */
  client?: ClientIdentity
  /**
   * Diccionario de claves→nombres del expediente que se está mirando. Lo que `resolveActor` hace
   * por los ids de usuario, esto lo hace por las claves de etapa, punto y campo de las entradas
   * anteriores al sello de etiquetas. Sin él la bitácora se lee genérica, nunca en claves.
   */
  labels?: AuditLabelResolver
}

const TIME_ZONE = 'America/Guatemala'

/**
 * Estilo visual por "tono" de evento. Paleta FRÍA (azules/cian/teal/índigo/pizarra) para un
 * look sobrio y coherente en claro y oscuro. Clases literales (no interpoladas) para que
 * Tailwind no las purgue. Cada tono trae el color del badge (icono blanco) y el título.
 */
const TONE_STYLES: Record<string, { badge: string; title: string }> = {
  blue: { badge: 'bg-blue-500', title: 'text-blue-600 dark:text-blue-400' },
  sky: { badge: 'bg-sky-500', title: 'text-sky-600 dark:text-sky-400' },
  cyan: { badge: 'bg-cyan-500', title: 'text-cyan-600 dark:text-cyan-400' },
  teal: { badge: 'bg-teal-500', title: 'text-teal-600 dark:text-teal-400' },
  indigo: { badge: 'bg-indigo-500', title: 'text-indigo-600 dark:text-indigo-400' },
  violet: { badge: 'bg-violet-500', title: 'text-violet-600 dark:text-violet-400' },
  slate: { badge: 'bg-slate-500', title: 'text-slate-600 dark:text-slate-300' },
  rose: { badge: 'bg-rose-500', title: 'text-rose-600 dark:text-rose-400' },
  gray: { badge: 'bg-muted-foreground', title: 'text-muted-foreground' }
}

/** Estilo de las tarjetas de metadata (motivo, comentario…), en tonos fríos y theme-aware. */
const CARD_STYLES: Record<NonNullable<AuditCard['tone']>, string> = {
  neutral: 'border-border bg-muted/40',
  info: 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/40',
  warning: 'border-indigo-200 bg-indigo-50 dark:border-indigo-900/50 dark:bg-indigo-950/40',
  danger: 'border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/40'
}

/** Mapa acción namespaced → icono + tono (frío). El default cubre acciones no mapeadas. */
const ACTION_VISUAL: Record<string, { icon: LucideIcon; tone: string }> = {
  'portal.created': { icon: FileText, tone: 'blue' },
  'portal.sent': { icon: Send, tone: 'sky' },
  'portal.email_opened': { icon: Mail, tone: 'indigo' },
  'portal.opened': { icon: Eye, tone: 'violet' },
  'portal.section_saved': { icon: Save, tone: 'cyan' },
  'portal.submitted': { icon: SendHorizontal, tone: 'teal' },
  'portal.resubmitted': { icon: CheckCheck, tone: 'teal' },
  'portal.review_taken_over': { icon: ClipboardCheck, tone: 'indigo' },
  'portal.review_started': { icon: ClipboardCheck, tone: 'indigo' },
  'portal.fields_reviewed': { icon: CheckSquare, tone: 'sky' },
  'portal.field_edited': { icon: Pencil, tone: 'violet' },
  'portal.changes_requested': { icon: MessageSquareWarning, tone: 'violet' },
  'portal.client_comment': { icon: MessageCircle, tone: 'blue' },
  'portal.approved': { icon: Check, tone: 'teal' },
  'portal.rejected': { icon: Ban, tone: 'rose' },
  'portal.revoked': { icon: XCircle, tone: 'slate' },
  'portal.reminder_sent': { icon: Bell, tone: 'indigo' },
  'portal.expired': { icon: CalendarX, tone: 'slate' },
  'portal.link_extended': { icon: RefreshCw, tone: 'sky' },
  'credit.started': { icon: FileText, tone: 'blue' },
  'credit.item_checked': { icon: CheckSquare, tone: 'teal' },
  'credit.field_answered': { icon: Pencil, tone: 'sky' },
  'credit.stage_advanced': { icon: CheckCheck, tone: 'indigo' },
  'credit.financing_type_set': { icon: RefreshCw, tone: 'violet' },
  'credit.approved': { icon: Check, tone: 'teal' },
  'credit.notice_skipped': { icon: Bell, tone: 'slate' },
  'credit.notice_sent': { icon: Send, tone: 'blue' },
  'credit.notice_failed': { icon: XCircle, tone: 'rose' }
}

const DEFAULT_VISUAL = { icon: FileText, tone: 'slate' as const }

/** Un ítem de la línea de tiempo: una entrada, posiblemente colapsando una racha de autosaves. */
type TimelineItem = {
  entry: AuditLogEntry
  /** Cuántos eventos idénticos consecutivos se fundieron en éste (1 = ninguno). */
  count: number
  /** occurredAt del evento MÁS ANTIGUO de la racha (para el rango en el tooltip). */
  earliest: string
}

/**
 * Colapsa rachas de autoguardados consecutivos de la MISMA sección en un solo ítem. El
 * autosave dispara un `section_saved` por cada flush del debounce, inundando la bitácora
 * (p.ej. "Datos personales" 63 veces). Fundirlos hace legible la línea de tiempo sin perder
 * el dato: se conserva el conteo y el rango temporal. Las entradas llegan más nuevas primero.
 */
function collapseAutosaves(entries: AuditLogEntry[]): TimelineItem[] {
  const items: TimelineItem[] = []
  for (const entry of entries) {
    const prev = items[items.length - 1]
    const isSave = entry.action === 'portal.section_saved'
    const sameRun =
      isSave &&
      prev?.entry.action === 'portal.section_saved' &&
      prev.entry.metadata?.section === entry.metadata?.section
    if (sameRun) {
      prev.count += 1
      prev.earliest = entry.occurredAt // orden nuevo→viejo: cada iteración es más antigua
      continue
    }
    items.push({ entry, count: 1, earliest: entry.occurredAt })
  }
  return items
}

const relativeTime = (iso: string): string => {
  try {
    return formatDistanceToNow(new TZDate(parseISO(iso), TIME_ZONE), {
      addSuffix: true,
      locale: es
    })
  } catch {
    return iso
  }
}

const exactTime = (iso: string): string => {
  try {
    return format(parseISO(iso), "d 'de' MMMM yyyy, HH:mm", { locale: es })
  } catch {
    return iso
  }
}

/**
 * Timeline de la bitácora (diseño "Historial"): hace visible el rastro de auditoría —
 * quién hizo qué, cuándo y con qué detalle. El backend devuelve los más recientes primero.
 */
export function AuditTimeline({
  entries,
  resolveActor = (id) => id,
  client,
  labels
}: AuditTimelineProps) {
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">Aún no hay actividad registrada.</p>
  }

  const items = collapseAutosaves(entries)

  return (
    <TooltipProvider delayDuration={200}>
      <ol className="flex flex-col">
        {items.map(({ entry, count, earliest }, index) => {
          const human = humanizeAuditEntry(entry, resolveActor, labels)
          const { icon: Icon, tone } = ACTION_VISUAL[entry.action] ?? DEFAULT_VISUAL
          const style = TONE_STYLES[tone] ?? TONE_STYLES.gray
          const isLast = index === items.length - 1
          const collapsed = count > 1

          // Racha colapsada: el tiempo mostrado es el de la última vez; el tooltip da el rango.
          const timeTooltip = collapsed
            ? `De ${exactTime(earliest)} a ${exactTime(human.occurredAt)}`
            : exactTime(human.occurredAt)

          // El cliente se nombra con su identidad real (primer nombre + primer apellido); el
          // interno con su nombre resuelto; el sistema con "Sistema".
          const actorName =
            human.actorType === 'client' ? (client?.name ?? human.actor) : human.actor

          return (
            <li key={entry.id} className="flex gap-3">
              {/* Rail: ÚNICO círculo por fila — badge con icono de acción + línea conectora */}
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                    style.badge
                  )}
                >
                  <Icon className="h-3.5 w-3.5 stroke-white" strokeWidth={2.5} />
                </span>
                {!isLast && <span className="w-px flex-1 bg-border" />}
              </div>

              {/* Contenido — estructura de aprobación de docs: label de color + tiempo, luego
                  la oración con el nombre del actor en negrita (tejido, sin repetir el actor). */}
              <div className={cn('flex flex-1 flex-col gap-1.5 pt-1', isLast ? 'pb-0' : 'pb-6')}>
                {/* ☠️ EL SEPARADOR ES TEXTO, NO UN `gap`. El título y el tiempo son dos elementos
                    HERMANOS sin un solo carácter entre ellos: la separación la ponía únicamente
                    `gap-x-2`, y un gap de CSS **no existe en el texto**. Al copiar el renglón, al
                    leerlo un lector de pantalla o al pegarlo en cualquier lado salía «Aviso
                    enviadohace alrededor de 1 hora», que es como lo reportó el lead. El `·` es un
                    carácter de verdad, así que sobrevive a la linealización.
                    `gap-y-1` es la otra mitad: la fila envuelve en un panel de 320 px y con
                    `leading-none` y sin gap vertical las dos líneas se tocaban. */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 leading-none">
                  <span className={cn('text-sm font-semibold', style.title)}>{human.label}</span>
                  {/* El separador viaja PEGADO al tiempo: si envuelve suelto, la primera línea
                      termina en un `·` colgando. */}
                  <span className="flex items-center gap-x-2">
                    <span aria-hidden="true" className="text-xs text-muted-foreground">
                      ·
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <time
                          dateTime={human.occurredAt}
                          className="cursor-default text-xs text-muted-foreground underline decoration-dotted underline-offset-2"
                        >
                          {relativeTime(human.occurredAt)}
                        </time>
                      </TooltipTrigger>
                      <TooltipContent>{timeTooltip}</TooltipContent>
                    </Tooltip>
                  </span>
                  {collapsed && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      ×{count}
                    </span>
                  )}
                </div>

                <p className="text-sm text-pretty text-foreground/80">
                  {human.subjectless ? (
                    human.predicate
                  ) : (
                    <>
                      <span className="font-bold text-foreground">{actorName}</span>{' '}
                      {human.predicate}
                    </>
                  )}
                  {human.detail && <span className="text-muted-foreground"> — {human.detail}</span>}
                </p>

                {human.cards?.map((card) => (
                  <div
                    key={`${card.title ?? 'card'}:${card.body}`}
                    className={cn('rounded-md border p-2.5', CARD_STYLES[card.tone ?? 'neutral'])}
                  >
                    {card.title && (
                      <p className="mb-0.5 text-xs font-semibold text-foreground/90">
                        {card.title}
                      </p>
                    )}
                    <p className="text-xs text-pretty text-foreground/80">{card.body}</p>
                  </div>
                ))}
              </div>
            </li>
          )
        })}
      </ol>
    </TooltipProvider>
  )
}
